import React, { useState, useRef, useEffect, useCallback } from 'react';
import './style.scss';

export default function HelpInformation({
  content = '',
  children,
  width = 240,
  placement = 'bottom-left',
  className = '',
  popoverClassName = '',
  popoverStyle = {},
}) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);

  const togglePopover = (e) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const adjustPosition = useCallback(() => {
    if (!popoverRef.current) return;
    const element = popoverRef.current;

    element.style.transform = '';
    const rect = element.getBoundingClientRect();
    const padding = 12;
    const viewportWidth = window.innerWidth;

    if (rect.right > viewportWidth - padding) {
      const overflowRight = rect.right - (viewportWidth - padding);
      const maxShiftLeft = rect.left - padding;
      const shift = Math.min(overflowRight, Math.max(0, maxShiftLeft));
      element.style.transform = `translateX(-${shift}px)`;
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    adjustPosition();

    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      adjustPosition();
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, adjustPosition]);

  const popoverBody = content || children;

  return (
    <div
      ref={wrapperRef}
      className={`help-information-wrapper ${className}`.trim()}
      onClick={(e) => e.stopPropagation()}
    >
      <span
        className={`help-icon ${isOpen ? 'is-active' : ''}`.trim()}
        onClick={togglePopover}
        role="button"
        tabIndex={0}
        aria-label="Help information"
        aria-expanded={isOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            togglePopover(e);
          }
        }}
      >
        ?
      </span>

      {isOpen && popoverBody && (
        <div
          ref={popoverRef}
          className={`help-popover placement-${placement} ${popoverClassName}`.trim()}
          style={{
            width: typeof width === 'number' ? `${width}px` : width,
            ...popoverStyle,
          }}
        >
          <div className="help-content">
            {typeof popoverBody === 'string' ? (
              <div dangerouslySetInnerHTML={{ __html: popoverBody }} />
            ) : (
              popoverBody
            )}
          </div>
        </div>
      )}
    </div>
  );
}
